import { FC, Fragment, ReactNode, useMemo, useState } from 'react';

import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { IconDownload, IconEye } from '@tabler/icons-react';

import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { ProtectedDownloadPurpose, SaleBidderLevel } from '__generated__/globalTypes';
import { PiiNoticeModal } from 'features/irm/PiiNoticeModal';
import { SupportModalV1 } from 'ui-kit/DraggableHelpButton/SupportModalV1';

import {
  CreateProtectedDownload,
  CreateProtectedDownloadVariables,
} from 'mutation/__generated__/createProtectedDownload';
import { CREATE_PROTECTED_DOWNLOAD } from 'mutation/createProtectedDownload';
import { SaleDocuments, SaleDocumentsVariables } from 'query/__generated__/saleDataRoom';
import { SALE_DOCUMENTS_QUERY } from 'query/saleDataRoom';

import {
  DOWNLOAD_ACTION_CLASS,
  LevelBadge,
  SaleDataRoomTree,
  VIEW_ACTION_CLASS,
} from './SaleDataRoomTree';
import { isDataRoomEmpty, SaleDataRoomDocument, scopeDataRoomToPool } from './saleDataRoom';
import { DocumentTypeFilter } from './saleDocumentTypes';
import { saleSectionPath, SaleSection } from './saleSections';
import { formatLocalDateTime } from './salesUtils';
import { ViewToggle, ViewToggleOption } from './SaleApprovalPanel';

export const DATA_ROOM_HEADING = 'Data room';
export const DATA_ROOM_EMPTY_COPY = 'No documents in the data room yet.';
export const POOL_DATA_ROOM_HEADING = 'Pool documents';
export const POOL_DATA_ROOM_EMPTY_COPY = 'No documents for this pool in your data room.';
export const WHOLE_DATA_ROOM_LINK = 'Whole data room';
export const DATA_ROOM_FAILED_COPY = 'Failed to load the data room.';
export const OPEN_FAILED_COPY = "Couldn't open the document. Try again.";
export const VIEW_POPUP_BLOCKED_COPY = 'Allow pop-ups for this site to view documents.';

const refusalCode = (error: unknown): unknown =>
  error instanceof ApolloError ? error.graphQLErrors[0]?.extensions?.code : undefined;

// api words these refusals for the bidder (access closed, level too low, revoked, try again).
// Any other error's message may be internal, so it gets the fixed line.
const BIDDER_WORDED_REFUSALS = new Set([
  'FORBIDDEN',
  'BAD_USER_INPUT',
  'NOT_FOUND',
  'ACCESS_REVOKED',
  'DOCUMENT_TOO_LARGE',
  'PROTECTED_DOWNLOAD_TIMEOUT',
  'PROTECTED_DOWNLOAD_FAILED',
]);

const refusalMessage = (error: unknown): string => {
  const message = error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined;
  return message && BIDDER_WORDED_REFUSALS.has(String(refusalCode(error)))
    ? message
    : OPEN_FAILED_COPY;
};

// A DOWNLOAD carries Content-Disposition: attachment, so following its link saves the launcher
// file and leaves this page where it is. Some browsers ignore a click on a link that isn't in
// the page, so the link is attached for the click.
const saveFile = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

type OpenRequest = { document: SaleDataRoomDocument; purpose: ProtectedDownloadPurpose };

// Kept above the tree, not in a row: a chip or a search that hides the row mid-request must not
// drop the notice api asks for.
const useOpenDocument = () => {
  const [noticeFor, setNoticeFor] = useState<OpenRequest | null>(null);
  const [openingIds, setOpeningIds] = useState<ReadonlySet<string>>(new Set());
  const [createProtectedDownload] = useMutation<
    CreateProtectedDownload,
    CreateProtectedDownloadVariables
  >(CREATE_PROTECTED_DOWNLOAD);

  // A VIEW's tab is opened before the request, while the click still counts as the user's, or the
  // browser blocks it; it is pointed at the copy once api issues one.
  const open = async (openRequest: OpenRequest, piiNoticeVersion?: string) => {
    const { document, purpose } = openRequest;
    const viewTab = purpose === ProtectedDownloadPurpose.VIEW ? window.open('', '_blank') : null;
    if (purpose === ProtectedDownloadPurpose.VIEW && !viewTab) {
      toast.error(VIEW_POPUP_BLOCKED_COPY);
      return;
    }
    setOpeningIds((ids) => new Set(ids).add(document.document_id));
    try {
      const { data } = await createProtectedDownload({
        variables: {
          documentId: document.document_id,
          purpose,
          ...(piiNoticeVersion !== undefined && { piiNoticeAcknowledged: true, piiNoticeVersion }),
        },
      });
      const url = data?.createProtectedDownload.url;
      if (!url) {
        throw new Error(OPEN_FAILED_COPY);
      }
      if (viewTab) {
        viewTab.opener = null;
        viewTab.location.href = url;
      } else {
        saveFile(url);
      }
    } catch (openError) {
      viewTab?.close();
      if (refusalCode(openError) === 'PII_NOTICE_REQUIRED') {
        setNoticeFor(openRequest);
      } else {
        toast.error(refusalMessage(openError));
      }
    } finally {
      setOpeningIds((ids) => {
        const rest = new Set(ids);
        rest.delete(document.document_id);
        return rest;
      });
    }
  };

  // Exhibit 6: the Special PII Notice before every Level 2 open, never remembered between opens. A
  // level this client doesn't know gets it too.
  const request = (openRequest: OpenRequest) => {
    if (openRequest.document.level !== SaleBidderLevel.L1) {
      setNoticeFor(openRequest);
    } else {
      void open(openRequest);
    }
  };

  const notice = noticeFor && (
    <PiiNoticeModal
      onAccept={(version) => {
        setNoticeFor(null);
        void open(noticeFor, version);
      }}
      onDecline={() => setNoticeFor(null)}
    />
  );

  return { request, openingIds, notice };
};

const OpenDocumentActions: FC<{
  document: SaleDataRoomDocument;
  opening: boolean;
  onRequest: (openRequest: OpenRequest) => void;
}> = ({ document, opening, onRequest }) => (
  <>
    <button
      type="button"
      aria-label={`View ${document.file_name}`}
      disabled={opening}
      onClick={() => onRequest({ document, purpose: ProtectedDownloadPurpose.VIEW })}
      className={VIEW_ACTION_CLASS}
    >
      <IconEye aria-hidden="true" size={16} stroke={1.75} />
      View
    </button>
    <button
      type="button"
      aria-label={`Download ${document.file_name}`}
      disabled={opening}
      onClick={() => onRequest({ document, purpose: ProtectedDownloadPurpose.DOWNLOAD })}
      className={DOWNLOAD_ACTION_CLASS}
    >
      <IconDownload aria-hidden="true" size={16} stroke={1.75} />
      Download
    </button>
  </>
);

const ACCESS_SCOPE: Record<SaleBidderLevel, string> = {
  [SaleBidderLevel.L1]: 'General information, no PII',
  [SaleBidderLevel.L2]: 'Full due diligence',
};

export const PII_OPEN_NOTE =
  'Level 2 files contain PII: you confirm the Special PII Notice each time you open one.';

// protect sets a copy's expiry from the sale's download period, and the key service also refuses
// any copy once the bidder's access ends: whichever comes first.
export const downloadedCopiesNote = (downloadPeriodHours: number) =>
  `Open for ${downloadPeriodHours} hours after download, and never after access ends`;

const TREE_ID = 'bidder-data-room-tree';

export const ACCESS_LEVEL_LABEL = 'Access level';
export const LEVEL_2_UNAVAILABLE_HEADLINE = "Level 2 isn't available on your access.";
// RFP SOW §3.8b, p. 43: Level 2 follows the FDIC's online application, vetting, an OFAC
// certification for each sale, a Security Deposit Agreement and the security deposit.
export const LEVEL_2_UNAVAILABLE_COPY =
  "Level 2 adds full due-diligence files and loan data with personal information. It requires the FDIC's online application and vetting: an OFAC certification for this sale, a signed security deposit agreement and the security deposit. Contact ORSNN, the FDIC's loan sale advisor for this sale, to apply.";
export const SUPPORT_EMAIL_LINK = 'Email support@orsnn.com';
const SUPPORT_EMAIL_HREF = 'mailto:support@orsnn.com';

const LINK_CLASS =
  'text-left text-pink-200 underline-offset-2 hover:text-pink-100 hover:underline light:text-pink-700 light:hover:text-pink-800';

const LEVEL_1_BIDDER_OPTIONS: ViewToggleOption[] = [
  { level: SaleBidderLevel.L1, label: 'Level 1' },
  {
    level: SaleBidderLevel.L2,
    label: 'Level 2',
    unavailableReason: (
      <>
        <strong className="font-semibold text-white light:text-gray-900">
          {LEVEL_2_UNAVAILABLE_HEADLINE}
        </strong>{' '}
        {LEVEL_2_UNAVAILABLE_COPY}{' '}
        <a href={SUPPORT_EMAIL_HREF} className={`${LINK_CLASS} underline`}>
          {SUPPORT_EMAIL_LINK}
        </a>
      </>
    ),
  },
];

// loan-service lets only the seller ask for another level's view, so a Level 2 bidder has no
// Level 1 view to switch to.
const AccessLevel: FC<{ level: SaleBidderLevel }> = ({ level }) =>
  level === SaleBidderLevel.L1 ? (
    <div className="flex items-center gap-2">
      <span aria-hidden="true" className="text-xs text-gray-400 light:text-gray-700">
        {ACCESS_LEVEL_LABEL}
      </span>
      <ViewToggle
        viewAs={SaleBidderLevel.L1}
        onChange={() => undefined}
        label={ACCESS_LEVEL_LABEL}
        options={LEVEL_1_BIDDER_OPTIONS}
      />
    </div>
  ) : (
    <p data-testid="bidder-access-level" className="text-xs text-gray-400 light:text-gray-700">
      {ACCESS_LEVEL_LABEL}:{' '}
      <span className="font-medium text-white light:text-gray-900">Level 2</span>
    </p>
  );

const HeaderCell: FC<{ term: string; children: ReactNode }> = ({ term, children }) => (
  <div className="flex flex-col gap-1 border-l border-background-subtle/60 px-4 py-3 first:border-l-0 light:border-gray-100">
    <dt className="text-[11px] uppercase tracking-[0.08em] text-gray-500 light:text-gray-700">
      {term}
    </dt>
    <dd className="flex items-center gap-2 text-[13px] leading-5 text-white light:text-gray-900">
      {children}
    </dd>
  </div>
);

const AccessHeader: FC<{
  saleKeys: string[];
  level: SaleBidderLevel | null;
  accessEndsAt?: string;
  downloadPeriodHours: number;
  saleLinks: ReactNode;
}> = ({ saleKeys, level, accessEndsAt, downloadPeriodHours, saleLinks }) => {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-stretch rounded-md border border-background-subtle bg-background-canvas light:border-gray-200 light:bg-white">
      <dl className="grid flex-[1_1_640px] grid-cols-[repeat(auto-fit,minmax(190px,1fr))]">
        {saleKeys.length > 0 && (
          <HeaderCell term="Sale keys">
            <span className="font-pt-mono">
              {saleKeys.map((saleKey, index) => (
                <Fragment key={saleKey}>
                  {index > 0 && ' · '}
                  <span className="whitespace-nowrap">{saleKey}</span>
                </Fragment>
              ))}
            </span>
          </HeaderCell>
        )}
        {level && (
          <HeaderCell term="Your access">
            <LevelBadge level={level} />
            {ACCESS_SCOPE[level]}
          </HeaderCell>
        )}
        {accessEndsAt && (
          <HeaderCell
            term={Date.parse(accessEndsAt) <= Date.now() ? 'Access ended' : 'Access ends'}
          >
            <span>
              {formatLocalDateTime(accessEndsAt)}{' '}
              <span className="whitespace-nowrap text-gray-500 light:text-gray-700">
                · bid deadline
              </span>
            </span>
          </HeaderCell>
        )}
        <HeaderCell term="Downloaded copies">
          {downloadedCopiesNote(downloadPeriodHours)}
        </HeaderCell>
      </dl>
      <div className="flex flex-none flex-col justify-center gap-1.5 border-l border-background-subtle px-5 py-3 text-[13px] light:border-gray-200">
        {saleLinks}
        <button type="button" onClick={() => setSupportOpen(true)} className={LINK_CLASS}>
          Help &amp; support
        </button>
      </div>
      {supportOpen && <SupportModalV1 onClose={() => setSupportOpen(false)} />}
    </div>
  );
};

// The bidder's data room at the level api grants it: an L1 bidder gets L1 documents only. Given a
// pool's offeringId, it shows that pool's folder alone, for the pool page.
export const BidderDataRoom: FC<{
  saleId: string;
  saleKeys: string[];
  level: SaleBidderLevel | null;
  accessEndsAt?: string;
  downloadPeriodHours: number;
  offeringId?: string;
}> = ({ saleId, saleKeys, level, accessEndsAt, downloadPeriodHours, offeringId }) => {
  const [typeFilter, setTypeFilter] = useState(DocumentTypeFilter.ALL);
  const [query, setQuery] = useState('');
  const { request, openingIds, notice } = useOpenDocument();
  const { data, loading } = useQuery<SaleDocuments, SaleDocumentsVariables>(SALE_DOCUMENTS_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
  });
  const saleDataRoom = data?.saleDocuments;
  const dataRoom = useMemo(
    () =>
      saleDataRoom && offeringId ? scopeDataRoomToPool(saleDataRoom, offeringId) : saleDataRoom,
    [offeringId, saleDataRoom],
  );
  const headingId = offeringId ? 'pool-data-room-heading' : 'bidder-data-room-heading';

  const body = (() => {
    if (loading && !dataRoom) {
      return <p className="text-sm text-gray-400 light:text-gray-700">Loading the data room…</p>;
    }
    if (!dataRoom) {
      return (
        <p className="text-sm text-status-error light:text-error-muted">{DATA_ROOM_FAILED_COPY}</p>
      );
    }
    if (isDataRoomEmpty(dataRoom)) {
      return (
        <p className="text-sm text-gray-400 light:text-gray-700">
          {offeringId ? POOL_DATA_ROOM_EMPTY_COPY : DATA_ROOM_EMPTY_COPY}
        </p>
      );
    }
    return (
      <SaleDataRoomTree
        dataRoom={dataRoom}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        query={query}
        onQueryChange={setQuery}
        renderDocumentAction={(document) => (
          <OpenDocumentActions
            document={document}
            opening={openingIds.has(document.document_id)}
            onRequest={request}
          />
        )}
      />
    );
  })();

  return (
    <section
      className="mt-6 flex max-w-[1040px] flex-col gap-4 font-heebo"
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className="text-lg font-medium text-white light:text-gray-900">
        {offeringId ? POOL_DATA_ROOM_HEADING : DATA_ROOM_HEADING}
      </h2>
      <AccessHeader
        saleKeys={saleKeys}
        level={level}
        accessEndsAt={accessEndsAt}
        downloadPeriodHours={downloadPeriodHours}
        saleLinks={
          // The Q&A log and the sale's own documents sit outside any pool's folder.
          offeringId ? (
            <Link to={saleSectionPath(saleId, SaleSection.DOCUMENTS)} className={LINK_CLASS}>
              {WHOLE_DATA_ROOM_LINK}
            </Link>
          ) : (
            <a
              href={`#${TREE_ID}`}
              onClick={(event) => {
                event.preventDefault();
                setQuery('');
                setTypeFilter(DocumentTypeFilter.QA_LOG);
                document.getElementById(TREE_ID)?.scrollIntoView({ block: 'start' });
              }}
              className={LINK_CLASS}
            >
              Q&amp;A log
            </a>
          )
        }
      />
      {level && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {level === SaleBidderLevel.L2 && (
            <p className="mr-auto text-[13px] text-gray-400 light:text-gray-700">{PII_OPEN_NOTE}</p>
          )}
          <AccessLevel level={level} />
        </div>
      )}
      <div id={TREE_ID}>{body}</div>
      {notice}
    </section>
  );
};
